import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  deleteAllNotifications,
  deleteNotifications,
  getNotifications,
  markAllNotificationsRead,
  markAllNotificationsUnread,
  markNotificationsRead,
  markNotificationsUnread,
  starNotifications,
} from '../../services/notificationService';
import {
  displayNotificationBody,
  formatNotificationDateTime,
  phaseLabel,
} from '../../utils/notificationDisplayUtils';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
  { key: 'starred', label: 'Starred' },
];

const PAGE_SIZE = 20;
const SELECT_ALL_PAGE_SIZE = 100;

function StarIcon({ filled }) {
  return (
    <svg
      className={`w-4 h-4 ${filled ? 'text-amber-400 fill-amber-400' : 'text-gray-400 dark:text-slate-500'}`}
      viewBox="0 0 24 24"
      stroke="currentColor"
      fill={filled ? 'currentColor' : 'none'}
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
      />
    </svg>
  );
}

function resolveNotificationPhase(phase, title) {
  let key = (phase || '').toLowerCase();
  if (!key && title) {
    const t = title.toLowerCase();
    if (t.includes('failed')) key = 'failed';
    else if (t.includes('started')) key = 'started';
    else if (t.includes('complete')) key = 'completed';
  }
  return key;
}

function PhaseBadge({ phase, title }) {
  const key = resolveNotificationPhase(phase, title);
  const label = phaseLabel(key);
  if (!label) return null;
  const styles = {
    started: 'text-sky-800 dark:text-sky-200 bg-sky-100 dark:bg-sky-900/50 border border-sky-200 dark:border-sky-800',
    completed: 'text-white bg-green-700 dark:bg-green-600 border border-green-700 dark:border-green-600',
    failed: 'text-white bg-red-700 dark:bg-red-600 border border-red-700 dark:border-red-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${styles[key] || 'text-gray-600 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700'}`}>
      {label}
    </span>
  );
}

function notificationRowClass(item) {
  const phase = resolveNotificationPhase(item.event_phase, item.title);
  if (phase === 'completed') {
    return item.is_read
      ? 'bg-green-50/60 dark:bg-green-950/15 border-l-[3px] border-l-green-300 dark:border-l-green-700'
      : 'bg-green-50 dark:bg-green-950/25 border-l-[3px] border-l-green-400 dark:border-l-green-600';
  }
  if (phase === 'failed') {
    return item.is_read
      ? 'bg-red-50/60 dark:bg-red-950/15 border-l-[3px] border-l-red-300 dark:border-l-red-700'
      : 'bg-red-50 dark:bg-red-950/25 border-l-[3px] border-l-red-400 dark:border-l-red-600';
  }
  if (!item.is_read) {
    return 'bg-indigo-50/50 dark:bg-indigo-500/5 border-l-[3px] border-l-indigo-500';
  }
  return '';
}

export default function NotificationModal({ open, onClose, onUnreadCountChange }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('unread');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [selectAllActive, setSelectAllActive] = useState(false);
  const [busy, setBusy] = useState(false);

  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const loadingRef = useRef(false);
  const selectAllRef = useRef(false);
  const filterRef = useRef(filter);

  const allSelected = total > 0 && selected.size >= total;
  const someSelected = selected.size > 0;
  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  const applyPage = useCallback((payload, append) => {
    const batch = payload.notifications || [];
    const totalCount = payload.total || 0;
    setTotal(totalCount);
    setItems((prev) => {
      if (!append) return batch;
      const seen = new Set(prev.map((n) => n.id));
      return [...prev, ...batch.filter((n) => !seen.has(n.id))];
    });
    hasMoreRef.current = pageRef.current * PAGE_SIZE < totalCount;
    if (selectAllRef.current) {
      setSelected((prev) => {
        const next = new Set(prev);
        batch.forEach((n) => next.add(n.id));
        return next;
      });
    }
    if (!append && onUnreadCountChange && typeof payload.unread_count === 'number') {
      onUnreadCountChange(payload.unread_count);
    }
  }, [onUnreadCountChange]);

  const fetchPage = useCallback(async (statusFilter, pageNum, pageLimit = PAGE_SIZE) => {
    const { data } = await getNotifications({
      status: statusFilter,
      page: pageNum,
      limit: pageLimit,
    });
    return data?.data || {};
  }, []);

  const reload = useCallback(async (statusFilter) => {
    loadingRef.current = true;
    setLoading(true);
    setLoadingMore(false);
    setError('');
    pageRef.current = 1;
    hasMoreRef.current = false;
    selectAllRef.current = false;
    setSelectAllActive(false);
    setSelected(new Set());
    try {
      const payload = await fetchPage(statusFilter, 1);
      applyPage(payload, false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load notifications.');
      setItems([]);
      setTotal(0);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [applyPage, fetchPage]);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;
    const nextPage = pageRef.current + 1;
    const statusFilter = filterRef.current;
    loadingRef.current = true;
    setLoadingMore(true);
    setError('');
    try {
      const payload = await fetchPage(statusFilter, nextPage);
      pageRef.current = nextPage;
      applyPage(payload, true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load more notifications.');
    } finally {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, [applyPage, fetchPage]);

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    selectAllRef.current = selectAllActive;
  }, [selectAllActive]);

  useEffect(() => {
    if (!open) return undefined;
    reload(filter);
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, filter, reload, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const node = sentinelRef.current;
    const root = listRef.current;
    if (!node || !root) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMore();
    }, { root, rootMargin: '120px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, items.length, loading, loadMore]);

  const runBulk = async (action) => {
    if (!selectAllActive && !selectedIds.length) return;
    setBusy(true);
    try {
      if (selectAllActive) {
        if (action === 'read') await markAllNotificationsRead(filter);
        if (action === 'unread') await markAllNotificationsUnread(filter);
        if (action === 'delete') await deleteAllNotifications(filter);
      } else {
        if (action === 'read') await markNotificationsRead(selectedIds);
        if (action === 'unread') await markNotificationsUnread(selectedIds);
        if (action === 'delete') await deleteNotifications(selectedIds);
      }
      await reload(filter);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  const toggleStar = async (e, item) => {
    e.stopPropagation();
    try {
      await starNotifications([item.id], !item.is_starred);
      setItems((prev) => prev.map((n) => (
        n.id === item.id ? { ...n, is_starred: !n.is_starred } : n
      )));
    } catch {
      setError('Could not update star.');
    }
  };

  const openNotification = async (item) => {
    if (!item.is_read) {
      try {
        await markNotificationsRead([item.id]);
        setItems((prev) => {
          const next = prev.map((n) => (
            n.id === item.id ? { ...n, is_read: true } : n
          ));
          if (onUnreadCountChange) {
            onUnreadCountChange(next.filter((n) => !n.is_read).length);
          }
          return next;
        });
      } catch {
        /* non-blocking */
      }
    }
    onClose();
    if (item.action_path) {
      navigate(item.action_path);
    }
  };

  const toggleSelectAll = async () => {
    if (allSelected || selectAllActive) {
      selectAllRef.current = false;
      setSelectAllActive(false);
      setSelected(new Set());
      return;
    }
    setBusy(true);
    setError('');
    try {
      const ids = new Set();
      let pageNum = 1;
      let totalCount = total;
      while (pageNum < 50) {
        const payload = await fetchPage(filter, pageNum, SELECT_ALL_PAGE_SIZE);
        const batch = payload.notifications || [];
        totalCount = payload.total || 0;
        batch.forEach((n) => ids.add(n.id));
        if (!batch.length || pageNum * SELECT_ALL_PAGE_SIZE >= totalCount) break;
        pageNum += 1;
      }
      selectAllRef.current = true;
      setSelectAllActive(true);
      setSelected(ids);
      setTotal(totalCount);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not select all notifications.');
    } finally {
      setBusy(false);
    }
  };

  const toggleSelect = (e, id) => {
    e.stopPropagation();
    selectAllRef.current = false;
    setSelectAllActive(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4">
      <button
        type="button"
        aria-label="Close notifications"
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-modal-title"
        className="relative w-full max-w-xl md:max-w-2xl lg:max-w-3xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col h-[min(88dvh,760px)] overflow-hidden"
      >
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <h2 id="notification-modal-title" className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white flex-1 min-w-0 truncate">
              Notifications
            </h2>
            <div className="flex items-center gap-2 shrink-0">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-slate-600 text-indigo-600 w-4 h-4"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  disabled={!total || loading || busy}
                />
                Select all
              </label>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  filter === f.key
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300'
                }`}
              >
                {f.label}
              </button>
            ))}
            {someSelected && (
              <div className="flex items-center gap-2 ml-auto">
                {['read', 'unread', 'delete'].map((action) => (
                  <button
                    key={action}
                    type="button"
                    disabled={busy}
                    onClick={() => runBulk(action)}
                    className={`px-3 py-1.5 text-sm font-semibold rounded-lg border disabled:opacity-50 ${
                      action === 'delete'
                        ? 'text-red-700 dark:text-red-300 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950/30'
                        : 'text-gray-700 dark:text-slate-200 border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    {action === 'read' ? 'Read' : action === 'unread' ? 'Unread' : 'Delete'}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div
          ref={listRef}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 sm:px-4 py-2 bg-white dark:bg-slate-900 [scrollbar-width:thin]"
        >
          {loading && (
            <p className="text-center text-sm text-gray-400 py-10">Loading…</p>
          )}
          {!loading && error && items.length === 0 && (
            <p className="text-center text-sm text-red-500 py-6 px-2">{error}</p>
          )}
          {!loading && !error && items.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-10">No notifications.</p>
          )}
          {items.length > 0 && (
          <div className="space-y-2">
          {items.map((item) => {
            const body = displayNotificationBody(item);
            const when = formatNotificationDateTime(item.created_at);
            const phase = resolveNotificationPhase(item.event_phase, item.title);
            const rowTint = notificationRowClass(item);
            return (
              <div
                key={item.id}
                className={`flex gap-2.5 py-2.5 px-2.5 rounded-lg overflow-hidden ${rowTint}`}
              >
                <input
                  type="checkbox"
                  className="mt-1 rounded border-gray-300 dark:border-slate-600 text-indigo-600 w-4 h-4 shrink-0"
                  checked={selected.has(item.id)}
                  onClick={(e) => toggleSelect(e, item.id)}
                  onChange={() => {}}
                  aria-label={`Select ${item.title}`}
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 min-w-0">
                    {!item.is_read && phase !== 'completed' && phase !== 'failed' && (
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" aria-hidden />
                    )}
                    {!item.is_read && phase === 'completed' && (
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" aria-hidden />
                    )}
                    {!item.is_read && phase === 'failed' && (
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" aria-hidden />
                    )}
                    <button
                      type="button"
                      onClick={() => openNotification(item)}
                      className={`flex-1 min-w-0 text-left text-sm leading-snug line-clamp-2 ${
                        item.is_read
                          ? 'font-medium text-gray-800 dark:text-slate-200'
                          : 'font-semibold text-gray-900 dark:text-white'
                      }`}
                    >
                      {item.title}
                    </button>
                    {item.action_path && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openNotification(item); }}
                        className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 shrink-0 px-2 py-1"
                      >
                        View
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => toggleStar(e, item)}
                      className="p-0.5 shrink-0"
                      aria-label={item.is_starred ? 'Unstar' : 'Star'}
                    >
                      <StarIcon filled={item.is_starred} />
                    </button>
                  </div>

                  {body && (
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 mt-1 line-clamp-2 leading-snug">
                      {body}
                    </p>
                  )}

                  <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400 dark:text-slate-500 tabular-nums">
                    <PhaseBadge phase={item.event_phase} title={item.title} />
                    {when && (
                      <>
                        <span className="text-gray-300 dark:text-slate-600">·</span>
                        <span>{when}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
          )}
          <div ref={sentinelRef} className="h-8 flex items-center justify-center">
            {loadingMore && (
              <p className="text-xs text-gray-400">Loading more…</p>
            )}
            {!loadingMore && error && items.length > 0 && (
              <p className="text-xs text-red-500">{error}</p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
